// File: frontend/src/config/apiBase.js
//
// Canonical resolver for the backend API base URL (Create React App).
//
// Rules (see production domain policy):
//   • Admin/browser app origin  → https://pesafii.net        (NOT set here)
//   • Backend API base URL      → https://api.pesafii.net    (this file)
//
// The retired DuckDNS domain (agrinal.duckdns.org) is force-rejected here so a
// stale build-time REACT_APP_API_URL can never send traffic to a host the
// browser can no longer resolve (ERR_NAME_NOT_RESOLVED). This is defense in
// depth — the deployment env should also be corrected to api.pesafii.net.
//
// Never point production at https://web.pesafii.net — that host does not exist.

const PROD_API_BASE = "https://api.pesafii.net";
const DEV_API_BASE = "http://localhost:5000";

export function resolveApiBase() {
  let base = String(process.env.REACT_APP_API_URL || "").trim().replace(/\/+$/, "");

  // Hard block the retired DuckDNS domain regardless of how it was configured.
  if (base && /duckdns\.org/i.test(base)) {
    // eslint-disable-next-line no-console
    console.error(
      `[api] REACT_APP_API_URL is set to a retired DuckDNS domain (${base}). ` +
        `Ignoring it and using ${PROD_API_BASE}. Update your deployment env ` +
        `(e.g. Vercel) to REACT_APP_API_URL=${PROD_API_BASE}.`
    );
    base = "";
  }

  if (!base) {
    base = process.env.NODE_ENV === "production" ? PROD_API_BASE : DEV_API_BASE;
    if (process.env.NODE_ENV === "production") {
      // eslint-disable-next-line no-console
      console.warn(
        `[api] REACT_APP_API_URL was not set at build time — defaulting to ${PROD_API_BASE}.`
      );
    }
  }

  return base;
}

export const API_BASE_URL = resolveApiBase();
export const API_URL = `${API_BASE_URL}/api`;

/**
 * Open the connection to the API origin before anything needs it.
 *
 * ── WHY THIS IS NOT A <link> IN index.html ─────────────────────────────────
 * It was, briefly, using `%REACT_APP_API_URL%`. Production shipped
 * `<link rel="preconnect" href="https://agrinal.duckdns.org">` — because that is
 * what the Vercel env var still holds, and the RAW variable has none of the
 * guarding above. The app itself was fine (resolveApiBase rejects the retired
 * domain), but the browser was being sent to warm a dead host, and the page
 * advertised a decommissioned domain to anyone reading its source.
 *
 * Sourcing the hint from API_BASE_URL means it can only ever point where the
 * requests actually go. A build-time hint would fire a little earlier; one that
 * fires slightly later and is CORRECT is worth more than one that is early and
 * wrong.
 *
 * Still ahead of the first authenticated call, which cannot happen until React
 * has mounted — so the DNS lookup, TCP handshake and TLS negotiation are already
 * under way by then. `crossorigin` is required: the real requests are
 * credentialed, and a connection opened without it is not reused for them.
 */
export function preconnectApiOrigin() {
  try {
    if (typeof document === "undefined" || !API_BASE_URL) return false;
    if (!/^https?:\/\//i.test(API_BASE_URL)) return false;
    const origin = new URL(API_BASE_URL).origin;
    // Same-origin needs no hint, and duplicates are pointless.
    if (typeof window !== "undefined" && origin === window.location.origin) return false;
    if (document.head.querySelector(`link[rel="preconnect"][href="${origin}"]`)) return false;

    for (const rel of ["preconnect", "dns-prefetch"]) {
      const link = document.createElement("link");
      link.rel = rel;
      link.href = origin;
      if (rel === "preconnect") link.crossOrigin = "anonymous";
      document.head.appendChild(link);
    }
    return true;
  } catch {
    // A resource hint is an optimisation. It must never be able to stop the app
    // from starting.
    return false;
  }
}
